// Mount-on-approach for the DE profile's stacked sections (and the secondary blocks inside one).
import { Button } from "@companyos/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";

/**
 * A section's body mounts (and makes its calls) when it is the one the URL names, or once the
 * person scrolls it near the viewport (or asks for it): opening a tab costs the open section's
 * calls, as a tab of its own did (acceptance 3: a screen load makes its own calls), and the rest
 * load as the person scrolls. Once mounted it stays.
 */
export function WhenNear({
  eager,
  label,
  children,
}: {
  eager: boolean;
  label: string;
  children: () => ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(eager);
  const armed = usePersonScrolled();
  useEffect(() => {
    if (eager) setNear(true);
  }, [eager]);
  useEffect(() => {
    const el = ref.current;
    if (near || !armed || !el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setNear(true);
      },
      { rootMargin: "200px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near, armed]);
  if (near) return <>{children()}</>;
  // Not a loading state (nothing is being read yet): room for the section, and a way in.
  return (
    <div ref={ref} data-slot="de-section-waiting" className="min-h-16">
      <Button variant="link" size="sm" className="px-0" onClick={() => setNear(true)}>
        Show {label.toLowerCase()}
      </Button>
    </div>
  );
}

/** True once the person scrolls by hand (wheel, touch or a key), never for a scroll the page made. */
function usePersonScrolled(): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (scrolled) return;
    const on = () => setScrolled(true);
    const events = ["wheel", "touchmove", "keydown"] as const;
    for (const e of events) window.addEventListener(e, on, { passive: true });
    return () => {
      for (const e of events) window.removeEventListener(e, on);
    };
  }, [scrolled]);
  return scrolled;
}
